import { PlayIcon } from "lucide-react";

import { useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";

import { isClipReady } from "../../model/clipState";
import { formatClock } from "../../model/formatClock";
import type { HighlightView } from "../../model/Highlight";
import { momentVictims } from "./momentHeroes";

type ClipThumbProps = {
  highlight: HighlightView;
  // Подпись кнопки для экранного диктора: «Смотреть: Тройное убийство».
  label: string;
  onOpen: () => void;
  // large — главный клип блока, medium — карточка, mini — превью в строке списка.
  size?: "large" | "medium" | "mini";
  badge?: string;
  className?: string;
};

// Превью момента 16:9. Клип готов — кадр-обложка, кнопка «смотреть» и длина клипа;
// клипа нет — портрет героя и тех, кого он убил, на тёмном фоне с пунктиром.
export const ClipThumb = ({
  highlight,
  label,
  onOpen,
  size = "medium",
  badge,
  className,
}: ClipThumbProps) => {
  const hero = useHeroLookup();
  const { clip } = highlight;
  const length = formatClock(clip.duration_s ?? highlight.duration_s);
  const badgeLabel = badge !== undefined && (
    <span
      className={cn(
        "absolute rounded-md bg-accent px-2 py-0.5 text-xs font-bold tracking-wide text-accent-foreground uppercase",
        size === "large" ? "top-3 left-3" : "top-2 left-2",
      )}
    >
      {badge}
    </span>
  );
  const duration = (
    <span className="absolute right-2 bottom-1.5 rounded bg-background/85 px-1.5 font-mono text-xs text-foreground">
      {length}
    </span>
  );
  if (isClipReady(highlight)) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={onOpen}
        className={cn(
          "group relative block aspect-video w-full overflow-hidden rounded-[10px] bg-black outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
          className,
        )}
      >
        {clip.poster_url !== null && (
          <img src={clip.poster_url} alt="" className="block size-full object-cover" />
        )}
        {badgeLabel}
        <span className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full bg-background/70 transition-colors group-hover:bg-background/85",
              size === "large" ? "size-[72px]" : size === "medium" ? "size-11" : "size-8",
            )}
          >
            <PlayIcon
              aria-hidden="true"
              className={cn(
                "fill-foreground text-foreground",
                size === "large" ? "size-8" : size === "medium" ? "size-5" : "size-4",
              )}
            />
          </span>
        </span>
        {duration}
      </button>
    );
  }
  const me = hero(highlight.hero_id);
  const victims = momentVictims(highlight).map(hero);
  const face = { large: "size-20", medium: "size-[54px]", mini: "size-[30px]" }[size];
  const victimFace = { large: "size-14", medium: "size-10", mini: "size-[22px]" }[size];
  return (
    <div
      data-slot="clip-placeholder"
      className={cn(
        "relative flex aspect-video w-full items-center justify-center gap-1.5 overflow-hidden rounded-[10px] border border-dashed border-border-strong bg-[radial-gradient(circle_at_30%_40%,var(--color-sidebar-active),var(--color-background)_70%)]",
        className,
      )}
    >
      {me.iconUrl !== null && (
        <img
          src={me.iconUrl}
          alt=""
          className={cn("rounded-[10px] border-2 border-accent", face)}
        />
      )}
      {victims.length > 0 && size !== "mini" && (
        <span aria-hidden="true" className="px-1 text-xl text-subtle">
          ›
        </span>
      )}
      {victims.map(
        (victim, index) =>
          victim.iconUrl !== null && (
            <img
              key={`${victim.id}-${index}`}
              src={victim.iconUrl}
              alt=""
              className={cn("rounded-lg opacity-85", victimFace)}
            />
          ),
      )}
      {badgeLabel}
      {duration}
    </div>
  );
};
